/**
 * Updated by Milos
 */

import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs/Subject';
import { takeUntil } from 'rxjs/operators';
import { GoogleAnalyticsService } from 'google-analytics.service';

import { User, UserData } from 'app/@core/interfaces/common/users';
import { PaymentApi } from 'app/@core/backend/common/api/CalorieFriend/payment.api';
import {
  SubscriptionPlan,
  SubscriptionPlanData,
} from 'app/@core/interfaces/common/CalorieFriend/subscriptionPlan';
import { FacebookPixelService } from 'app/@theme/services/FacebookPixelService';
import { UserStore } from 'app/@core/stores/user.store';
import { NativeAppService } from 'app/@core/backend/common/services/native-app.service';
import { NbDialogService } from '@nebular/theme';
import { ConfirmMessageDialogComponent } from 'app/@components/confirm-message-dialog/confirm-message-dialog.component';

declare var Stripe: any;

@Component({
  selector: 'app-subscription-plan',
  templateUrl: './subscription-plan.component.html',
  styleUrls: ['./subscription-plan.component.scss'],
})
export class SubscriptionPlanComponent implements OnDestroy {
  waiting: boolean = true;
  currentUser: User;
  subscriptionPlans: SubscriptionPlan[] = [];

  private readonly unsubscribe$ = new Subject<void>();
  private allSubscriptionPlans: SubscriptionPlan[] = [];
  private stripeKey: string;

  constructor(
    private route: ActivatedRoute,
    private usersService: UserData,
    private userStore: UserStore,
    private paymentApi: PaymentApi,
    private facebookPixelService: FacebookPixelService,
    private googleAnalyticsService: GoogleAnalyticsService,
    subscriptionService: SubscriptionPlanData,
    public nativeAppService: NativeAppService,
    public dialogService: NbDialogService
  ) {
    // get stripe key
    paymentApi
      .getStripeKey()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res) => (this.stripeKey = res.key));

    // get all/selectable subscription plans
    subscriptionService
      .list()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((plans) => {
        this.allSubscriptionPlans = plans;
        this.subscriptionPlans = plans.filter((x) => x.hasPlanChoices);

        if (userStore.settings(true)) this.checkSelectedPlan();
        else
          userStore
            .onUserStateChange()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(() => this.checkSelectedPlan());
      });

    this.checkPlanSucceed();
  }

  isFromApp() {
    return this.nativeAppService.isFromApp();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private checkPlanSucceed(): void {
    const success = this.route.snapshot.queryParamMap.get('success');
    if (success === 'true') {
      const package_name =
        this.route.snapshot.queryParamMap.get('package_name');
      const currency = this.route.snapshot.queryParamMap.get('currency');
      const price = this.route.snapshot.queryParamMap.get('price');

      // fb pixel
      this.facebookPixelService.load(FacebookPixelService.PurchaseEvent, {
        content_name: package_name,
        currency: currency,
        value: price,
      });

      // GA
      const gaLabel = `content_name=${package_name}&currency=${currency}&value=${price}`;
      this.googleAnalyticsService.eventEmitter(
        FacebookPixelService.PurchaseEvent,
        'Subscription',
        'Pay',
        gaLabel,
      );
    }
  }

  private checkSelectedPlan(): void {
    const user = this.userStore.getUser();
    if (!user) return;
    this.currentUser = user;

    const choicablePlan = this.subscriptionPlans.find(
      (x) => x.planCode === user.selectedPlan,
    );
    if (!choicablePlan) {
      const planToPush = this.allSubscriptionPlans.find(
        (x) => x.planCode === this.currentUser.selectedPlan,
      );
      if (planToPush) this.subscriptionPlans = [planToPush].concat(this.subscriptionPlans);
    }

    this.waiting = false;
  }

  onClickManageInvalid(e: Event, planCode: string) {
    const plan = this.subscriptionPlans.find((a) => a.planCode === planCode);

    this.onSelect(e, plan);
  }

  onSelect(e: Event, plan: SubscriptionPlan) {
    e.preventDefault();

    this.usersService.getCurrentUser().subscribe((user) => {
      this.waiting = true;

      if (!plan.priceId || plan.planCode === 'free') {
        // cancel

        //MUST CHECK PATIUENT COUNT AND ASK TO ACTIVE HERE. 


        this.waiting = false;
        this.dialogService
            .open(ConfirmMessageDialogComponent, {
              closeOnEsc: true,
              closeOnBackdropClick: true,
              context: {
                Message:
                  'Reverting to free plan will cancel your subscription and some clients will lose access to their app. Are you sure?',
              },
            })
            .onClose.subscribe((result) => {
              if (result) {
                this.paymentApi.cancelSubscription().subscribe(
                  (data) => {
                    this.refreshCurrentUser();
                  },
                  (error) => {
                    this.refreshCurrentUser();
                  },
                );
              }
            });


       
      } else {
        if (
          this.currentUser.selectedPlan === 'free' ||
          !this.currentUser.planSubscription
        ) {
          // new one
          const stripe = Stripe(this.stripeKey);

          // You'll have to define PRICE_ID as a price ID before this code block
          this.createCheckoutSession(plan.priceId).then(function (data) {
            // Call Stripe.js method to redirect to the new Checkout page
            stripe
              .redirectToCheckout({
                sessionId: data.sessionId,
              })
              .then((result) => {
                console.log(result);
              })
              .catch((error) => {
                console.log(error);
              })
              .finally(() => {
                this.waiting = false;
              });
          });
        } else {
          // upgrade
          this.paymentApi.updateSubscription(plan.priceId).subscribe(
            (data) => {
              this.refreshCurrentUser();
            },
            (error) => {
              console.log(error);
              this.waiting = false;
            },
          );
        }
      }
    });
  }

  private refreshCurrentUser() {
    setTimeout(() => {
      this.usersService.getCurrentUser().subscribe((user) => {
        this.currentUser = user;
        this.waiting = false;
        this.userStore.setUser(user);
      });
    }, 100);
  }

  private createCheckoutSession(priceId): Promise<any> {
    return this.paymentApi.createCheckoutSession(priceId).toPromise();
  }

  private updatePaymentInformation() {


    if (this.currentUser.selectedPlan === 'ltd199-trial') {
      window.location.href = 'https://go.trainerfriend.com/ltd';
      return;
    }


    const stripe = Stripe(this.stripeKey);

    // You'll have to define PRICE_ID as a price ID before this code block
    this.createCheckoutSession('').then(function (data) {
      // Call Stripe.js method to redirect to the new Checkout page
      stripe
        .redirectToCheckout({
          sessionId: data.sessionId,
        })
        .then((result) => {
          console.log(result);
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          this.waiting = false;
        });
    });
  }


}
